import { Container, Row, Col, Accordion, AccordionItem } from "react-bootstrap";
import { PageTitle } from "./Controls/PageTitle";
import { useLoaderData, useNavigate } from "react-router-dom";
import { FacilityInfo, ListedPlan, SubscribeUpdateReq, SubscribeUpdateInitResp, Subscription, Vehicle, AccessPeriodInfo } from "./Data/ApiTypes";
import { useEffect, useState } from "react";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { LocalDate } from "@js-joda/core";
import { MonthlyParkingModify } from "./Controls/MonthlyParkingModify";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "./Data/ApiStripe";
import { StripeMonthlyUpdateCheckoutForm } from "./StripeMonthlyUpdateCheckoutForm";
import { StripeElementsOptions } from "@stripe/stripe-js";
import { appearanceOptions, stripeFonts } from "./Controls/StripeAppearance";
import { Button } from "./Controls/Button";
import { pluralize } from "./Data/English";
import { Money, showMoney } from "./Data/Money";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { QuantityUpdateReqApi, SubscriptionStatus } from "./Data/ApiTransport";
import { WaitlistNoticeModal } from "./WaitlistNoticeModal";
import { toast } from "react-toastify";
import { AddSpacesModal } from "./AddSpacesModal";

export function UserPlanDetailsChangePage() {
    const nav = useNavigate();
    const [ facility,       setFacility       ] = useState<FacilityInfo>();
    const [ selMonthlyPlan, setSelMonthlyPlan ] = useState<ListedPlan>();
    const [ readyToPay,     setReadyToPay     ] = useState( false );
    const [ resp,           setResp           ] = useState<SubscribeUpdateInitResp>();
    const [ sub,            vehicles          ] = useLoaderData() as [Subscription, Vehicle[]];
    const [ modSub,         setModSub         ] = useState<Subscription>( { ...sub } );
    const [ paymentVis,     setPaymentVis     ] = useState<"hidden" | "visible">( "hidden" );
    const [ confirmSaveReduce,    setConfirmSaveReduce ] = useState( false );
    const [ confirmDefer,   setConfirmDefer   ] = useState( false );
    const [ terms,          setTerms          ] = useState<string>();
    const [ proratedFee,    setProratedFee    ] = useState<Money>();    
    const [showWaitlistModal, setShowWaitlistModal] = useState(false);
    const [showAddSpacesModal, setShowAddSpacesModal] = useState(false);

    const isActive = ( ap: AccessPeriodInfo ) => {        
        const now = LocalDate.now();
        const startOk = ap.start.isEqual( now ) || ap.start.isBefore( now );
        const endOk   = now.isBefore( ap.end );
        return startOk && endOk;
    }
    const accessPeriodQty = sub.accessPeriods.filter( isActive ).map( ap => ap.quantity ).reduce( (a, b) => a + b, 0 );
    const plan = sub.plan;

    //some pre-computing

    const spacesAdded  = Math.max( 0, modSub.quantity - accessPeriodQty );
    const disabled     = modSub.quantity === sub.quantity && sub.status == SubscriptionStatus.Active;
    const showSaveReduce = !sub.isAccountsReceivable && spacesAdded === 0;
    const showContinue = !sub.isAccountsReceivable && spacesAdded >   0 && !readyToPay
    const showDefer    = sub.isAccountsReceivable 
    const cls          = `w-100 ${ disabled ? "border-secondary text-muted" : "" }`;

    useEffect( () => {
        const getPlanDetails = async () => {
            setFacility( sub.facility );
            const res = await Api.getPlan( sub.plan.planId );
            processResult( res,
                val => {
                 
                    setSelMonthlyPlan( val );
                },
                err => console.log( `Problem retrieving facility: ${err}`) ); //ignore for now
            
        }
        const getTerms = async () => {
            const res = await Api.planTermsGet( sub.plan.planId );
            processResult( res,
                val => setTerms( val ),
                err => console.log( `Problem retrieving terms: ${err}`) ); //ignore for now
        }
        getPlanDetails();
        getTerms();
    }, [] );

    useEffect( () => {
        async function subscribePreview() {
            const res = await Api.modifySubscriptionPreview( sub.subscriptionId, { start: LocalDate.now()!, quantity: spacesAdded! } );
            if( res.isOk ) {
                setProratedFee( res.value.price );
            }
        }
        if( !selMonthlyPlan )
            return;
        subscribePreview();
    }, [selMonthlyPlan, modSub.quantity] );

    useEffect( () => {
        if( readyToPay ) {
            const updateReq: SubscribeUpdateReq = {
                facilityId:     facility!.facilityId,
                subscriptionId: sub.subscriptionId,
                newPlanEntryId: selMonthlyPlan?.planEntryId!,
                newQuantity:    modSub.quantity,
            };
            Api.subscriptionUpdateInit( updateReq ).then(
                res => processResult( res,
                    val => {
                        setResp( val );
                        setShowAddSpacesModal(true);
                    },
                    err => {
                    if (err == 'QuantityDecrease')
                        setConfirmSaveReduce(true)
                    if (err === 'Waitlisted') {
                    setShowWaitlistModal(true)
                    }
                    if (err === 'InvalidWaitlistSpotsQuantity') {
                        toast.error('The quantity you are requesting exceeds the available waitlist quantity.');
                        nav(-1);
                    }
                }))
        }
    }, [readyToPay] );

    function updateSubscriptionQuantity() {
    try {
        const updateReq: QuantityUpdateReqApi = {
            subscriptionId: sub.subscriptionId,
            newQuantity:    modSub.quantity,
        };
        const response = Api.subscriptionUpdateNextCycle(updateReq).then(
            res => processResult( res,
                val => {
                    toast.success("Subscription quantity updated successfully!");
                    nav( `/user/plan/${sub.subscriptionId}` );
                },
                err => console.error( err ) ) );;

        return response; 
    } catch (error) {
        toast.error("Failed to update subscription quantity");
    }
    }

    function saveChanges(): void {
        const updateReq: SubscribeUpdateReq = {
            facilityId:     facility!.facilityId,
            subscriptionId: sub.subscriptionId,
            newPlanEntryId: selMonthlyPlan?.planEntryId!,
            newQuantity:    modSub.quantity,
        };
        Api.subscriptionUpdateDecr( updateReq ).then(
            res => processResult( res,
                val => {
                    nav( `/user/plan/${sub.subscriptionId}` );
                },
                err => console.error( err ) ) );
    }

    function billMe(): void {
        const updateReq: SubscribeUpdateReq = {
            facilityId:     facility!.facilityId,
            subscriptionId: sub.subscriptionId,
            newPlanEntryId: selMonthlyPlan?.planEntryId!,
            newQuantity:    modSub.quantity,
        };
        Api.subscriptionUpdateDefer( updateReq ).then(
            res => processResult( res,
                val => {
                    nav( `/user/plan/${sub.subscriptionId}` );
                },
                err => console.error( err ) ) );
    }

    function handleDeferClick(): void {
        // If the current subscription setting is the same as the access period quantity, allow them to add spaces immediately
        if (spacesAdded > 0 && sub.quantity === accessPeriodQty) {
            setShowAddSpacesModal(true);
        } else if (sub.quantity !== accessPeriodQty) {
            // If the quantity is different from the current, they've added spaces to the next cycle already, so we can't invoice others now
            updateSubscriptionQuantity();
        } else {
            setConfirmDefer(true);
        }
    }

    const options: StripeElementsOptions = {
        clientSecret:                resp?.paymentIntentClientSecret!,
        customerSessionClientSecret: resp?.customerSessionClientSecret!,
        appearance:                  appearanceOptions,
        fonts:                       stripeFonts
    };

    if( !facility || !selMonthlyPlan || !proratedFee ) {
        return <></>;
    }

    return <Container fluid className="pb-3">
        <ConfirmModal title={"Confirm Spot Reduction"}
                      message={<div className="d-flex flex-column gap-3">
                          <div>Are you sure you want to reduce your parking to {modSub.quantity} spot?</div>
                          <div>The change will take effect on the next
                              next {sub.isAccountsReceivable ? "billing" : "processing"} date.
                          </div>
                      </div>}
                      show={confirmSaveReduce}
                      setShow={setConfirmSaveReduce}
                      confirmText="Save Changes"
                      cancelText="Cancel"
                      onConfirm={ () => saveChanges() } 
                      onCancel={() => nav(-1)}
                      />

        <ConfirmModal title={"Confirm Invoice"}
                      message={<div className="d-flex flex-column gap-3">
                          <div className="fw-bold">
                              Confirm your request to amend your monthly subscription?</div>
                          <div>
                              An invoice will be generated immediately and is subject to the below terms and conditions.
                          </div>
                          <Accordion>
                            <AccordionItem eventKey={"terms"}>
                                <Accordion.Header>
                                    Terms and Conditions
                                </Accordion.Header>
                                <Accordion.Body style={{ height: "16em", overflowY: "scroll" }}>
                                    <div 
                                        dangerouslySetInnerHTML={{ 
                                            __html: terms?.trim() === "" ? "No terms have been set" : terms || "" 
                                        }}
                                    />
                                </Accordion.Body>
                            </AccordionItem>
                          </Accordion>
                      </div>}
                      show={confirmDefer}
                      setShow={setConfirmDefer}
                      confirmText={`Confirm`}
                      cancelText="Cancel"
                      onConfirm={ () => billMe() } />
                    <WaitlistNoticeModal
                        onCancel={() => {setShowWaitlistModal(false); nav(-1)}}
                        show={showWaitlistModal}
                        requestPayload={{
                            parkerId: modSub.parkerId,
                            facilityId: facility!.facilityId,
                            planEntryId: modSub!.planEntryId,
                            spacesRequested: spacesAdded,
                            isAddingToExistingParker: true,
                            subscriptionId: modSub.subscriptionId,
                        }}
                    ></WaitlistNoticeModal>
                    <AddSpacesModal
                        show={showAddSpacesModal}
                        onClose={() => {setShowAddSpacesModal(false); nav(-1)}}
                        onConfirm={(option) => {
                            setShowAddSpacesModal(false);
                            if (option === "immediate") {
                                if (sub.isAccountsReceivable) { 
                                    setConfirmDefer(true);
                                } else {
                                    setPaymentVis( "visible" );
                                }
                            } else {
                              updateSubscriptionQuantity();
                            }
                        }}
                    />

        <Row>
            <Col>
                <PageTitle>
                    Modify Plan at {facility.name}
                </PageTitle>
                <Container className="g-0">
                    <Row>
                        <Col xl="12">
                            <MonthlyParkingModify
                                selOpt={selMonthlyPlan}
                                subscriptionId={sub.subscriptionId}
                                start={LocalDate.now()}
                                disabled={readyToPay}
                                originalQuantity={accessPeriodQty}
                                quantity={modSub.quantity}
                                proratedFee={proratedFee}
                                isAch={false} // ACH not supported for modifying. Takes too long to clear, make them pay with card
                                setQuantity={ n => setModSub( { ...modSub, quantity: n } ) } />
                            <div className="mt-2 d-flex justify-content-end gap-1 text-end" style={{ display: "grid", gridAutoColumns: "1fr" }}>
                                {!readyToPay && <Button className="w-100"
                                        onClick={ () => nav( `/user/plan/${sub.subscriptionId}` ) }>
                                    Cancel
                                </Button>}
                                {showDefer && <Button className={cls}
                                                      disabled={disabled}
                                                      onClick={handleDeferClick}>
                                    Continue
                                </Button>}
                                {showSaveReduce && <Button className={cls}
                                                     disabled={disabled}
                                                     onClick={ () => setConfirmSaveReduce( true ) }>
                                    Continue
                                </Button>}
                                {showContinue && <Button className={`${cls} btn-main`}
                                                         disabled={disabled}
                                                         onClick={ () => setReadyToPay( true )}>
                                    Continue
                                </Button>}
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs="12">
                            <div style={{ visibility: paymentVis }}>
                                {resp && <Elements stripe={stripePromise} options={options}>
                                    <StripeMonthlyUpdateCheckoutForm init={resp} subscriptionId={sub.subscriptionId} waitlistEntryId={resp.waitlistEntryId} />
                                </Elements>}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </Col>
        </Row>
    </Container>;
}
